// Deviation Wizard Service - connects to Postgres via Azure Function
import { authService } from '../auth/authService';

class DeviationWizardService {
  constructor() {
    // Use dedicated wolfwalker-api Function App (Python Flex Consumption with VNet)
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    
    // Azure Function endpoints for deviation wizard (to be created)
    this.deviationApiUrl = `${this.functionAppUrl}/api/deviation_wizard`;
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken();
      const userInfo = authService.getUserInfo();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown'
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  /**
   * Get deviation wizard data
   * Returns survey deviation calculations and corrections
   */
  async getDeviationData(options = {}) {
    try {
      const {
        limit = 100,
        offset = 0
      } = options;

      const headers = await this.getAuthenticatedHeaders();
      let url = `${this.deviationApiUrl}?limit=${limit}&offset=${offset}`;

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch deviation wizard data: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        success: result.success,
        data: result.data || [],
        message: result.message
      };
    } catch (error) {
      console.error('Error fetching deviation wizard data:', error);
      throw error;
    }
  }

  /**
   * Get all dashboard data for display
   */
  async getDashboardData() {
    try {
      const deviationData = await this.getDeviationData({ limit: 100 });

      return {
        deviationData: deviationData.data,
        success: true
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  /**
   * Filter data by search term
   */
  filterData(data, searchTerm) {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item => 
      JSON.stringify(item).toLowerCase().includes(term)
    );
  }

  /**
   * Sort data by field and direction
   */
  sortData(data, field, direction = 'asc') {
    return [...data].sort((a, b) => {
      const aVal = a[field] ?? '';
      const bVal = b[field] ?? '';
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }

  /**
   * Calculate deviation compensation for planned drillhole
   * @param {Object} params - Calculation parameters
   * @param {number} params.azDeg - Azimuth in degrees (0-360)
   * @param {number} params.dipDeg - Dip in degrees (-90 to 90)
   * @param {number} params.easting - UTM Easting coordinate
   * @param {number} params.northing - UTM Northing coordinate
   * @param {number} params.depthM - Target depth in meters
   * @param {string} [params.project='default'] - Project name
   * @param {number} [params.searchRadius] - Optional search radius in meters
   * @param {number} [params.minPairs=10] - Minimum number of historical pairs
   * @param {boolean} [params.expand=false] - Enable radius expansion
   * @param {number} [params.step] - Expansion step size (required if expand=true)
   * @param {number} [params.maxRadius] - Maximum radius (required if expand=true)
   * @returns {Promise<Object>} Calculation result with compensated angles and metadata
   */
  async calculateDeviationCompensation(params) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const url = `${this.functionAppUrl}/api/hexbin_offsets`;

      const requestBody = {
        az_deg: params.azDeg,
        dip_deg: params.dipDeg,
        easting: params.easting,
        northing: params.northing,
        depth_m: params.depthM,
        project: params.project || 'default',
        min_pairs: params.minPairs !== undefined ? params.minPairs : 10,
        expand: params.expand || false,
        blend_mode: params.blendMode || 'blend',
        include_neighbors: params.includeNeighbors !== undefined ? params.includeNeighbors : true,
        max_neighbors: params.maxNeighbors !== undefined ? params.maxNeighbors : 300,
        use_directional_weight: params.useDirectionalWeight !== undefined ? params.useDirectionalWeight : true,
        dir_exponent: params.dirExponent !== undefined ? params.dirExponent : 2.0,
        use_distance_weight: params.useDistanceWeight !== undefined ? params.useDistanceWeight : true,
        rate_intervals: params.rateIntervals !== undefined ? params.rateIntervals : '10,100'
      };

      // Add optional search_radius if provided
      if (params.searchRadius !== undefined && params.searchRadius !== null && params.searchRadius !== '') {
        requestBody.search_radius = parseFloat(params.searchRadius);
      }

      // Add expand parameters if expand mode is enabled
      if (requestBody.expand) {
        if (params.step !== undefined && params.step !== null && params.step !== '') {
          requestBody.step = parseFloat(params.step);
        }
        if (params.maxRadius !== undefined && params.maxRadius !== null && params.maxRadius !== '') {
          requestBody.max_radius = parseFloat(params.maxRadius);
        }
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to calculate deviation compensation: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calculating deviation compensation:', error);
      throw error;
    }
  }

  /**
   * Refresh deviation metrics materialized views
   * Updates backend statistics with latest drilling data
   * @returns {Promise<Object>} Refresh operation result
   */
  async refreshDeviationMetrics() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const url = `${this.functionAppUrl}/api/hexbin_offsets`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          refresh: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to refresh deviation metrics: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error refreshing deviation metrics:', error);
      throw error;
    }
  }

  /**
   * Legacy method - kept for backward compatibility
   * @deprecated Use calculateDeviationCompensation instead
   */
  async calculateHexbinOffset(azDeg, dipDeg, easting, northing, depthM = 650.0) {
    return this.calculateDeviationCompensation({
      azDeg, dipDeg, easting, northing, depthM
    });
  }
}

// Export singleton instance
export const deviationWizardService = new DeviationWizardService();
export default deviationWizardService;
